import React, { useState, useEffect, useRef } from "react";
import { makeStyles, useTheme, Dropdown } from "@seahorse/ui";
import BestXExampleTradeLoader, {
  BestXExampleTrade,
} from "./BestXExampleTradeLoader";
import {
  BESTX_EARLIEST_EXECUTION_MS,
  BESTX_EXECUTION_RANGE_ERROR,
} from "./bestxDateDefaults";
import {
  fromDateInputValue,
  fromZonedDateTimeInputValue,
  toUtcDateTimeValue,
  getDateTimeInputZoneLabel,
  getExecutionTimestampBounds,
  toDateInputValue,
  toZonedDateTimeInputValue,
} from "../shared/dateTimeInput";
import { useDisplayTimeZone } from "../../Utils/displayTimeZone";

type BestXLandingField =
  | "currencyPair"
  | "executionTimestamp"
  | "executionSpotRate"
  | "executionForwardPoints"
  | "expirationTimestamp";

const COMPACT_EXAMPLE_TRADE_QUERY =
  "(max-width: 800px) and (max-height: 800px)";

type BestXLandingPrefill = {
  currencyPair?: string;
  executionTimestamp?: string;
  executionSpotRate?: number | null;
  executionForwardPoints?: number | null;
  expirationTimestamp?: string;
};

interface BestXLandingPageProps {
  currencyList: string[];
  onSubmit: (trade: {
    currencyPair: string;
    executionTimestamp: string;
    executionSpotRate: number | null;
    executionForwardPoints: number | null;
    expirationTimestamp: string;
  }) => void;
  initialTrade?: BestXLandingPrefill;
  notice?: string;
  requiredFields?: BestXLandingField[];
}

const BestXLandingPage: React.FC<BestXLandingPageProps> = ({
  currencyList,
  onSubmit,
  initialTrade,
  notice,
  requiredFields = [],
}) => {
  const theme = useTheme();
  const [useInlineExampleTrade, setUseInlineExampleTrade] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia(COMPACT_EXAMPLE_TRADE_QUERY).matches
  );

  const [currencyPair, setCurrencyPair] = useState("");
  // Completion time is held in UTC; the input shows the display zone.
  const [executionTimestamp, setExecutionTimestamp] = useState("");
  const [executionSpotRate, setExecutionSpotRate] = useState("");
  const [executionForwardPoints, setExecutionForwardPoints] = useState("");
  const [expirationTimestamp, setExpirationTimestamp] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [exampleNotice, setExampleNotice] = useState("");
  const [highlightedFields, setHighlightedFields] = useState<
    BestXLandingField[]
  >([]);
  const exampleHighlightTimerRef = useRef<number | null>(null);
  const { timeZone } = useDisplayTimeZone();
  const executionInput = toZonedDateTimeInputValue(executionTimestamp, timeZone);
  const { minExecutionTimestamp, maxExecutionTimestamp } =
    getExecutionTimestampBounds(timeZone, BESTX_EARLIEST_EXECUTION_MS);

  const [dropdownItems, setDropdownItems] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    if (currencyList?.length) {
      setDropdownItems(
        [...currencyList]
          .sort((a, b) => a.localeCompare(b))
          .map((currency) => ({ label: currency, value: currency }))
      );
    }
  }, [currencyList]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(COMPACT_EXAMPLE_TRADE_QUERY);
    const updateExampleTradePlacement = () =>
      setUseInlineExampleTrade(mediaQuery.matches);

    updateExampleTradePlacement();
    mediaQuery.addEventListener("change", updateExampleTradePlacement);
    return () =>
      mediaQuery.removeEventListener("change", updateExampleTradePlacement);
  }, []);

  useEffect(() => {
    if (!initialTrade) return;

    setCurrencyPair(initialTrade.currencyPair ?? "");
    setExecutionTimestamp(initialTrade.executionTimestamp ?? "");
    setExecutionSpotRate(
      initialTrade.executionSpotRate == null
        ? ""
        : String(initialTrade.executionSpotRate)
    );
    setExecutionForwardPoints(
      initialTrade.executionForwardPoints == null
        ? ""
        : String(initialTrade.executionForwardPoints)
    );
    setExpirationTimestamp(
      toDateInputValue(initialTrade.expirationTimestamp)
    );
  }, [initialTrade]);

  useEffect(() => {
    return () => {
      if (exampleHighlightTimerRef.current !== null) {
        window.clearTimeout(exampleHighlightTimerRef.current);
      }
    };
  }, []);

  const requiresField = (field: BestXLandingField) =>
    requiredFields.includes(field);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!currencyPair) next.currencyPair = "Currency pair is required";
    if (!executionInput) {
      next.executionTimestamp = "Completion time is required.";
    } else if (
      executionInput < minExecutionTimestamp ||
      executionInput > maxExecutionTimestamp
    ) {
      next.executionTimestamp = BESTX_EXECUTION_RANGE_ERROR;
    }
    if (requiresField("executionSpotRate") && !executionSpotRate.trim()) {
      next.executionSpotRate = "Execution spot rate is required";
    } else if (
      executionSpotRate.trim() &&
      !Number.isFinite(Number(executionSpotRate))
    ) {
      next.executionSpotRate = "Execution spot rate must be a valid number";
    }
    if (
      requiresField("executionForwardPoints") &&
      !executionForwardPoints.trim()
    ) {
      next.executionForwardPoints = "Execution forward points are required";
    } else if (
      executionForwardPoints.trim() &&
      !Number.isFinite(Number(executionForwardPoints))
    ) {
      next.executionForwardPoints =
        "Execution forward points must be a valid number";
    }
    if (requiresField("expirationTimestamp") && !expirationTimestamp.trim()) {
      next.expirationTimestamp = "Value date is required.";
    }
    if (
      expirationTimestamp &&
      executionInput &&
      expirationTimestamp < toDateInputValue(executionInput)
    ) {
      next.expirationTimestamp =
        "Value date must be on or after the completion time.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const parseOptionalNumber = (value: string) => {
      if (!value.trim()) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    onSubmit({
      currencyPair,
      executionTimestamp: toUtcDateTimeValue(executionTimestamp),
      executionSpotRate: parseOptionalNumber(executionSpotRate),
      executionForwardPoints: parseOptionalNumber(executionForwardPoints),
      expirationTimestamp: fromDateInputValue(expirationTimestamp),
    });
  };

  // Keep examples separate from submit so analysis is always user-initiated.
  const handleExampleTradeSelect = (trade: BestXExampleTrade) => {
    const populatedFields: BestXLandingField[] = [
      "currencyPair",
      "executionTimestamp",
      "executionSpotRate",
      "executionForwardPoints",
    ];

    if (trade.expirationTimestamp) {
      populatedFields.push("expirationTimestamp");
    }

    setCurrencyPair(trade.currencyPair);
    setExecutionTimestamp(trade.executionTimestamp);
    setExecutionSpotRate(String(trade.executionSpotRate));
    setExecutionForwardPoints(String(trade.executionForwardPoints));
    setExpirationTimestamp(toDateInputValue(trade.expirationTimestamp));
    setErrors({});
    setExampleNotice("Example trade loaded");
    setHighlightedFields(populatedFields);

    if (exampleHighlightTimerRef.current !== null) {
      window.clearTimeout(exampleHighlightTimerRef.current);
    }
    exampleHighlightTimerRef.current = window.setTimeout(() => {
      setHighlightedFields([]);
      setExampleNotice("");
      exampleHighlightTimerRef.current = null;
    }, 1800);
  };

  const useStyles = makeStyles({
    wrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      width: "100%",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
      padding: 24,
      position: "relative",
    },
    card: {
      width: "100%",
      maxWidth: 480,
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.04)",
      padding: "32px 28px",
      display: "flex",
      flexDirection: "column",
      gap: 20,
    },
    header: {
      textAlign: "center" as const,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    exampleTradeRow: {
      display: "flex",
      justifyContent: "flex-end",
    },
    title: {
      color: "#E0E0E0",
      fontSize: 20,
      fontWeight: 700,
      margin: 0,
    },
    subtitle: {
      color: "#E0E0E0",
      fontSize: 13,
      opacity: 0.6,
      margin: 0,
    },
    notice: {
      borderRadius: 10,
      border: "1px solid rgba(93, 173, 226, 0.4)",
      background: "rgba(93, 173, 226, 0.12)",
      color: "#D6EAF8",
      padding: "12px 14px",
      fontSize: 12,
      lineHeight: "18px",
    },
    confirmation: {
      color: "#B9E6C8",
      fontSize: 12,
      lineHeight: "16px",
      margin: 0,
      textAlign: "center" as const,
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      borderRadius: 8,
      transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    },
    fieldGroupHighlighted: {
      background: "rgba(93, 173, 226, 0.1)",
      boxShadow: "0 0 0 2px rgba(93, 173, 226, 0.38)",
    },
    label: {
      color: "#E0E0E0",
      fontSize: 13,
      fontWeight: 600,
      opacity: 0.9,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      fontSize: 14,
      outline: "none",
      "&::placeholder": {
        color: "rgba(224,224,224,0.35)",
      },
    },
    error: {
      color: "#ff6b6b",
      fontSize: 12,
      margin: 0,
    },
    button: {
      marginTop: 8,
      cursor: "pointer",
      padding: "12px 0",
      borderRadius: 10,
      border: "none",
      background: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
      fontSize: 15,
      fontWeight: 700,
      transition: "opacity 0.15s ease",
      "&:hover": {
        opacity: 0.9,
      },
    },
    optionalHint: {
      color: "#E0E0E0",
      fontSize: 11,
      opacity: 0.45,
      fontWeight: 400,
    },
  });

  const classes = useStyles();
  const fieldGroupClassName = (field: BestXLandingField) =>
    `${classes.fieldGroup} ${
      highlightedFields.includes(field) ? classes.fieldGroupHighlighted : ""
    }`;

  return (
    <div className={classes.wrapper}>
      {!useInlineExampleTrade && (
        <BestXExampleTradeLoader onSelect={handleExampleTradeSelect} />
      )}
      <div className={classes.card}>
        {useInlineExampleTrade && (
          <div className={classes.exampleTradeRow}>
            <BestXExampleTradeLoader
              onSelect={handleExampleTradeSelect}
              placement="inline"
            />
          </div>
        )}
        <div className={classes.header}>
          <p className={classes.title}>Trade Analysis</p>
          <p className={classes.subtitle}>
            Enter the details of the trade you want to analyze.
          </p>
        </div>
        {notice && <div className={classes.notice}>{notice}</div>}
        {exampleNotice && (
          <p className={classes.confirmation} role="status">
            {exampleNotice}
          </p>
        )}

        {/* Currency Pair */}
        <div className={fieldGroupClassName("currencyPair")}>
          <span className={classes.label}>Currency Pair</span>
          <Dropdown
            items={dropdownItems}
            value={currencyPair}
            onChange={(value: any) => {
              setCurrencyPair(value);
              setErrors((prev) => ({ ...prev, currencyPair: "" }));
            }}
            placeholder="Select currency pair"
            fullWidth
          />
          {errors.currencyPair && (
            <p className={classes.error}>{errors.currencyPair}</p>
          )}
        </div>

        {/* Completion timestamp */}
        <div className={fieldGroupClassName("executionTimestamp")}>
          <span className={classes.label}>
            Completion Time ({getDateTimeInputZoneLabel(executionTimestamp, timeZone)})
          </span>
          <input
            className={classes.input}
            type="datetime-local"
            step={60}
            min={minExecutionTimestamp}
            max={maxExecutionTimestamp}
            value={executionInput}
            onChange={(e) => {
              setExecutionTimestamp(
                fromZonedDateTimeInputValue(e.target.value, timeZone)
              );
              setErrors((prev) => ({
                ...prev,
                executionTimestamp: "",
                expirationTimestamp: "",
              }));
            }}
          />
          {errors.executionTimestamp && (
            <p className={classes.error}>{errors.executionTimestamp}</p>
          )}
        </div>

        {/* Execution Spot Rate */}
        <div className={fieldGroupClassName("executionSpotRate")}>
          <span className={classes.label}>
            Execution Spot Rate{" "}
            <span className={classes.optionalHint}>
              {requiresField("executionSpotRate") ? "(required)" : "(optional)"}
            </span>
          </span>
          <input
            className={classes.input}
            value={executionSpotRate}
            onChange={(e) => {
              setExecutionSpotRate(e.target.value);
              setErrors((prev) => ({ ...prev, executionSpotRate: "" }));
            }}
            placeholder="e.g. 157.96"
            inputMode="decimal"
          />
          {errors.executionSpotRate && (
            <p className={classes.error}>{errors.executionSpotRate}</p>
          )}
        </div>

        {/* Execution Forward Points */}
        <div className={fieldGroupClassName("executionForwardPoints")}>
          <span className={classes.label}>
            Execution Forward Points{" "}
            <span className={classes.optionalHint}>
              {requiresField("executionForwardPoints")
                ? "(required)"
                : "(optional)"}
            </span>
          </span>
          <input
            className={classes.input}
            value={executionForwardPoints}
            onChange={(e) => {
              setExecutionForwardPoints(e.target.value);
              setErrors((prev) => ({ ...prev, executionForwardPoints: "" }));
            }}
            placeholder="e.g. -0.0002"
            inputMode="decimal"
          />
          {errors.executionForwardPoints && (
            <p className={classes.error}>{errors.executionForwardPoints}</p>
          )}
        </div>

        {/* Value date */}
        <div className={fieldGroupClassName("expirationTimestamp")}>
          <span className={classes.label}>
            Value Date{" "}
            <span className={classes.optionalHint}>
              {requiresField("expirationTimestamp") ? "(required)" : "(optional)"}
            </span>
          </span>
          <input
            className={classes.input}
            type="date"
            min={toDateInputValue(executionInput) || undefined}
            value={expirationTimestamp}
            onChange={(e) => {
              setExpirationTimestamp(e.target.value);
              setErrors((prev) => ({ ...prev, expirationTimestamp: "" }));
            }}
          />
          {errors.expirationTimestamp && (
            <p className={classes.error}>{errors.expirationTimestamp}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="button"
          className={classes.button}
          onClick={handleSubmit}
        >
          Analyze Trade
        </button>
      </div>
    </div>
  );
};

export default BestXLandingPage;
